import React, { useState, useEffect } from "react";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { Search, Calendar, ChevronDown, ChevronUp, CreditCard, ShoppingBag, MessageSquare } from "lucide-react";
import { useCompanyLogos } from "../../hooks/useCompanyLogos";
import { useAuth } from "../../context/AuthContext";
import { logSecurityEvent } from "../../services/AuditLogger";
import OrderNotesModal from "../modals/OrderNotesModal";

export default function OrderHistoryReport() {
  const { currentUser } = useAuth();
  const [pos, setPos] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPoId, setExpandedPoId] = useState(null);
  const [selectedNotesPo, setSelectedNotesPo] = useState(null);
  const { companyLogos } = useCompanyLogos();

  // Filters state
  const [companyFilter, setCompanyFilter] = useState("All");
  const [poStatusFilter, setPoStatusFilter] = useState("All");
  const [logisticsStatusFilter, setLogisticsStatusFilter] = useState("All");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    setLoading(true);
    
    // Subscribe to POs
    const unsubscribePos = onSnapshot(collection(db, "pos"), (snapshot) => {
      const poList = [];
      snapshot.forEach((doc) => {
        poList.push({ id: doc.id, ...doc.data() });
      });
      setPos(poList);
      setLoading(false);
    }, (err) => {
      console.error("Error subscribing to POs:", err);
      setLoading(false);
    });

    // Subscribe to Logistics
    const unsubscribeLogistics = onSnapshot(collection(db, "logistics"), (snapshot) => {
      const shipList = [];
      snapshot.forEach((doc) => {
        shipList.push({ id: doc.id, ...doc.data() });
      });
      setShipments(shipList);
    }, (err) => {
      console.error("Error subscribing to logistics:", err);
    });

    return () => {
      unsubscribePos();
      unsubscribeLogistics();
    };
  }, []);

  const getPoShipment = (poId) => {
    return shipments.find(s => s.poId === poId);
  };

  const getPoLogoUrl = (po) => {
    if (po.logoUrl) return po.logoUrl;
    const activeLogo = companyLogos.find(l => l.isActive);
    return activeLogo?.url || "";
  };

  const getCompanyNameForPo = (po) => {
    if (po.companyName) return po.companyName;
    if (po.logoUrl) {
      const match = companyLogos.find(l => l.url === po.logoUrl);
      if (match) return match.name;
    }
    const activeLogo = companyLogos.find(l => l.isActive);
    return activeLogo?.name || "Global";
  };

  const getPoOrderedDate = (po, shipment) => {
    if (shipment && shipment.orderedDate) {
      return shipment.orderedDate;
    }
    // Check if any items have an ordered date
    if (shipment?.itemStatuses) {
      const dates = Object.values(shipment.itemStatuses)
        .map(i => i.orderedDate)
        .filter(Boolean);
      if (dates.length > 0) {
        return dates.sort()[0]; // Return the earliest ordered date
      }
    }
    return "";
  };

  const getPoOverallLogisticsStatus = (po, shipment) => {
    if (po.status === "Lost") return "N/A";
    if (!shipment) return "To Order";
    const raw = shipment.status || "To Order";
    return raw === "Transport -> HQ" ? "Transport -> NC" : raw;
  };

  const getLineItemLogisticsStatus = (item, shipment) => {
    if (!shipment) return "To Order";
    const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [item];
    const statusesList = itemVendors.map(v => {
      const key = v.id && v.id !== 'default' ? `${item.id}_${v.id}` : item.id;
      const raw = shipment.itemStatuses?.[key]?.status || shipment.status || "To Order";
      return raw === "Transport -> HQ" ? "Transport -> NC" : raw;
    });
    const unique = [...new Set(statusesList)];
    if (unique.length === 1) return unique[0];
    const statuses = ["To Order", "Purchased", "Transport -> NC", "Warehouse", "Transport -> Client", "Delivered"];
    const indices = unique.map(s => statuses.indexOf(s)).filter(i => i >= 0);
    if (indices.length === 0) return "To Order";
    const lowest = statuses[Math.min(...indices)];
    return `${lowest} (Mixed)`;
  };

  const handleUpdatePaymentStatus = async (poId, status) => {
    try {
      const poRef = doc(db, "pos", poId);
      await updateDoc(poRef, { paymentStatus: status });
    } catch (err) {
      console.error("Error updating payment status:", err);
      alert("Failed to update payment status.");
    }
  };

  const countPoNotes = (po) => {
    if (!po) return 0;
    let count = 0;
    if (po.logs && Array.isArray(po.logs)) count += po.logs.length;
    if (po.notes && typeof po.notes === "string" && po.notes.trim() !== "") count += 1;
    if (po.updateReason && typeof po.updateReason === "string" && po.updateReason.trim() !== "") count += 1;
    if (po.changeHistory && Array.isArray(po.changeHistory)) count += po.changeHistory.length;
    if (po.items && Array.isArray(po.items)) {
      po.items.forEach(item => {
        if (item.logs && Array.isArray(item.logs)) count += item.logs.length;
        if (item.notes && typeof item.notes === "string" && item.notes.trim() !== "") count += 1;
        if (item.vendors && Array.isArray(item.vendors)) {
          item.vendors.forEach(v => {
            if (v.logs && Array.isArray(v.logs)) count += v.logs.length;
          });
        }
      });
    }
    return count;
  };

  const handleSavePoNote = async (poId, noteText) => {
    const po = pos.find(p => p.id === poId);
    if (!po) return;
    const newLog = {
      text: noteText,
      author: currentUser?.displayName || currentUser?.email || "Staff",
      timestamp: new Date().toISOString(),
      source: "Order History Report",
      type: "Report Note"
    };
    const poRef = doc(db, "pos", poId);
    const updatedLogs = [...(po.logs || []), newLog];
    await updateDoc(poRef, { logs: updatedLogs });

    setSelectedNotesPo(prev => prev && prev.id === poId ? { ...prev, logs: updatedLogs } : prev);

    await logSecurityEvent({
      action: "PO_NOTE_ADDED",
      category: "PROCUREMENT",
      userId: currentUser?.uid || "anonymous",
      userEmail: currentUser?.email || "anonymous",
      resource: `pos/${poId}`,
      status: "SUCCESS",
      details: { poNumber: po.poNumber, noteText }
    });
  };

  // Filter logic
  const filteredPos = pos.filter(po => {
    const shipment = getPoShipment(po.id);
    const orderedDate = getPoOrderedDate(po, shipment);

    // 0. Company Filter
    if (companyFilter !== "All") {
      const poCompany = getCompanyNameForPo(po);
      if (poCompany.toLowerCase() !== companyFilter.toLowerCase()) return false;
    }

    // 1. PO Status Filter
    if (poStatusFilter !== "All" && po.status !== poStatusFilter) return false;

    // 2. Logistics Status Filter
    const overallLogisticsStatus = getPoOverallLogisticsStatus(po, shipment);
    if (logisticsStatusFilter !== "All") {
      // Match if the overall logistics status matches, OR if any of the items match this status
      const hasMatchingItem = po.items && po.items.some(item => 
        getLineItemLogisticsStatus(item, shipment) === logisticsStatusFilter
      );
      if (overallLogisticsStatus !== logisticsStatusFilter && !hasMatchingItem) {
        return false;
      }
    }

    // 3. Payment Status Filter
    const payStatus = po.paymentStatus || "Unpaid";
    if (paymentStatusFilter !== "All" && payStatus !== paymentStatusFilter) return false;

    // 4. Date range filter on orderedDate
    if (startDate || endDate) {
      if (!orderedDate) return false; // Exclude POs that aren't ordered yet when date range is active
      const ordTime = new Date(orderedDate).getTime();
      if (startDate) {
        const startTime = new Date(startDate).getTime();
        if (ordTime < startTime) return false;
      }
      if (endDate) {
        const endTime = new Date(endDate);
        endTime.setHours(23, 59, 59, 999);
        if (ordTime > endTime.getTime()) return false;
      }
    }

    // 5. Search Text Filter (Customer Name, Vendor Name, or PO Number)
    if (searchTerm.trim() !== "") {
      const searchLower = searchTerm.toLowerCase();
      const matchesPoNo = po.poNumber && po.poNumber.toLowerCase().includes(searchLower);
      const matchesCustomer = po.clientName && po.clientName.toLowerCase().includes(searchLower);
      
      const matchesVendor = po.items && po.items.some(item => {
        if (item.vendors && item.vendors.length > 0) {
          return item.vendors.some(v => v.vendor && v.vendor.toLowerCase().includes(searchLower));
        }
        return item.vendor && item.vendor.toLowerCase().includes(searchLower);
      });

      if (!matchesPoNo && !matchesCustomer && !matchesVendor) return false;
    }

    return true;
  });

  const getPoUniqueVendors = (po) => {
    if (!po.items || po.items.length === 0) return "N/A";
    const vendors = [...new Set(po.items.flatMap(item => {
      if (item.vendors && item.vendors.length > 0) {
        return item.vendors.map(v => v.vendor);
      }
      return [item.vendor];
    }).filter(Boolean))];
    return vendors.length > 0 ? vendors.join(", ") : "N/A";
  };

  const getPoStatusBadgeClass = (status) => {
    switch (status) {
      case "Won": return "badge-success";
      case "Lost": return "badge-danger";
      case "Bid Submitted": return "badge-info";
      case "Draft Bid": return "badge-warning";
      case "Received": return "badge-secondary";
      default: return "";
    }
  };

  const getLogisticsBadgeClass = (status) => {
    switch (status) {
      case "Delivered": return "badge-success";
      case "To Order": return "badge-secondary";
      case "Purchased": return "badge-info";
      case "Warehouse": return "badge-warning";
      case "N/A": return "";
      default: return "badge-primary"; // Transport statuses
    }
  };

  const getPaymentBadgeClass = (status) => {
    switch (status) {
      case "Paid": return "badge-success";
      case "Refunded": return "badge-danger";
      case "Partial": return "badge-warning";
      default: return "badge-secondary"; // Unpaid
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "3rem" }}>
        <p style={{ color: "var(--text-secondary)" }}>Loading order history...</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      
      {/* Search & Filters Controls */}
      <div className="card glass-card" style={{ padding: "1.5rem" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "1rem" }}>
          
          {/* Text Search */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Search size={14} /> Search
            </label>
            <input
              type="text"
              className="form-input"
              placeholder="PO #, Customer, or Vendor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Company Filter */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Company</label>
            <select
              className="form-input"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              style={{ margin: 0 }}
            >
              <option value="All">All Companies</option>
              {companyLogos.map(logo => (
                <option key={logo.id} value={logo.name}>
                  {logo.name}
                </option>
              ))}
            </select>
          </div>

          {/* PO Status Filter */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">PO Status</label>
            <select
              className="form-input"
              value={poStatusFilter}
              onChange={(e) => setPoStatusFilter(e.target.value)}
              style={{ margin: 0 }}
            >
              <option value="All">All PO Statuses</option>
              <option value="Received">Received</option>
              <option value="Draft Bid">Draft Bid</option>
              <option value="Bid Submitted">Bid Submitted</option>
              <option value="Won">Won</option>
              <option value="Lost">Lost</option>
            </select>
          </div>

          {/* Logistics Status Filter */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Logistics Status</label>
            <select
              className="form-input"
              value={logisticsStatusFilter}
              onChange={(e) => setLogisticsStatusFilter(e.target.value)}
              style={{ margin: 0 }}
            >
              <option value="All">All Logistics Statuses</option>
              <option value="To Order">To Order</option>
              <option value="Purchased">Purchased</option>
              <option value="Transport -> NC">{"Transport -> NC"}</option>
              <option value="Warehouse">Warehouse</option>
              <option value="Transport -> Client">{"Transport -> Client"}</option>
              <option value="Delivered">Delivered</option>
            </select>
          </div>

          {/* Payment Status Filter */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Payment Status</label>
            <select
              className="form-input"
              value={paymentStatusFilter}
              onChange={(e) => setPaymentStatusFilter(e.target.value)}
              style={{ margin: 0 }}
            >
              <option value="All">All Payment Statuses</option>
              <option value="Unpaid">Unpaid</option>
              <option value="Paid">Paid</option>
              <option value="Partial">Partial</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>

          {/* Ordered Date Pickers */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Calendar size={14} /> Start Order Date
            </label>
            <input
              type="date"
              className="form-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ margin: 0 }}
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
              <Calendar size={14} /> End Order Date
            </label>
            <input
              type="date"
              className="form-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ margin: 0 }}
            />
          </div>

        </div>
      </div>

      {/* Orders Accordion Table */}
      <div className="card glass-card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="table-responsive">
          <table className="reports-table" style={{ margin: 0, width: "100%", borderCollapse: "collapse" }}>
             <thead>
              <tr>
                <th style={{ width: "60px", paddingLeft: "1.5rem" }}>Logo</th>
                <th style={{ paddingLeft: "0.5rem" }}>PO Number</th>
                <th>Order Date</th>
                <th>Customer Name</th>
                <th>Vendor Name</th>
                <th>PO Status</th>
                <th>Logistics Status</th>
                <th>Payment Status</th>
                <th style={{ width: "70px", textAlign: "center" }}>Notes</th>
                <th style={{ width: "50px" }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredPos.length === 0 ? (
                <tr>
                  <td colSpan="10" className="empty-state" style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
                    No matching orders found.
                  </td>
                </tr>
              ) : (
                filteredPos.map(po => {
                  const shipment = getPoShipment(po.id);
                  const orderedDate = getPoOrderedDate(po, shipment);
                  const overallLogStatus = getPoOverallLogisticsStatus(po, shipment);
                  const payStatus = po.paymentStatus || "Unpaid";
                  const isExpanded = expandedPoId === po.id;
                  const notesCount = countPoNotes(po);

                  return (
                    <React.Fragment key={po.id}>
                      <tr 
                        onClick={() => setExpandedPoId(isExpanded ? null : po.id)}
                        style={{ cursor: "pointer", borderBottom: isExpanded ? "none" : "1px solid var(--border-color)" }}
                      >
                        <td style={{ paddingLeft: "1.5rem", verticalAlign: "middle" }}>
                          {getPoLogoUrl(po) ? (
                            <img 
                              src={getPoLogoUrl(po)} 
                              alt="Company logo" 
                              style={{ width: "24px", height: "24px", borderRadius: "4px", objectFit: "cover", display: "block" }} 
                            />
                          ) : (
                            <div style={{ width: "24px", height: "24px", borderRadius: "4px", backgroundColor: "var(--bg-input)" }} />
                          )}
                        </td>
                        <td style={{ paddingLeft: "0.5rem", fontWeight: "600", color: "var(--text-primary)" }}>
                          {po.poNumber}
                        </td>
                        <td>{orderedDate || <span style={{ fontStyle: "italic", color: "var(--text-muted)", fontSize: "0.85rem" }}>Not Ordered</span>}</td>
                        <td>{po.clientName}</td>
                        <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={getPoUniqueVendors(po)}>
                          {getPoUniqueVendors(po)}
                        </td>
                        <td>
                          <span className={`badge ${getPoStatusBadgeClass(po.status)}`}>
                            {po.status}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${getLogisticsBadgeClass(overallLogStatus)}`}>
                            {overallLogStatus}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${getPaymentBadgeClass(payStatus)}`}>
                            {payStatus}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedNotesPo(po);
                            }}
                            title={`View all notes for PO ${po.poNumber} (${notesCount} note${notesCount === 1 ? "" : "s"})`}
                            style={{
                              background: notesCount > 0 ? "rgba(59, 130, 246, 0.12)" : "transparent",
                              color: notesCount > 0 ? "var(--primary)" : "var(--text-muted)",
                              border: notesCount > 0 ? "1px solid rgba(59, 130, 246, 0.3)" : "1px solid transparent",
                              borderRadius: "4px",
                              padding: "0.25rem 0.45rem",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "0.3rem",
                              cursor: "pointer",
                              fontSize: "0.78rem",
                              fontWeight: 600,
                              transition: "all 0.15s ease"
                            }}
                          >
                            <MessageSquare size={14} />
                            {notesCount > 0 && <span>{notesCount}</span>}
                          </button>
                        </td>
                        <td style={{ textAlign: "right", paddingRight: "1.5rem" }}>
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </td>
                      </tr>

                      {/* Expanded Accordion Panel */}
                      {isExpanded && (
                        <tr style={{ backgroundColor: "rgba(255, 255, 255, 0.01)", borderBottom: "1px solid var(--border-color)" }}>
                          <td colSpan="10" style={{ padding: "1.5rem 2.5rem" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                              
                              {/* Metadata & Quick Actions */}
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem" }}>
                                <div>
                                  <h4 style={{ margin: "0 0 0.5rem 0", color: "var(--text-primary)", fontSize: "0.95rem" }}>Shipping Destination</h4>
                                  <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-secondary)", lineHeight: "1.4" }}>
                                    {po.shipToAddress || "No shipping address specified."}
                                    {po.shipToAddress2 ? `, ${po.shipToAddress2}` : ""}
                                    {po.shipToCity ? `, ${po.shipToCity}` : ""}
                                    {po.shipToState ? `, ${po.shipToState}` : ""}
                                    {po.shipToZip ? ` ${po.shipToZip}` : ""}
                                  </p>
                                </div>

                                <div style={{ display: "flex", gap: "1.5rem", alignItems: "center" }}>
                                  
                                  {/* Payment Method Display */}
                                  <div>
                                    <h4 style={{ margin: "0 0 0.5rem 0", color: "var(--text-primary)", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                      Payment Method
                                    </h4>
                                    <p style={{ margin: 0, fontSize: "0.85rem", fontWeight: "600", color: "var(--primary)" }}>
                                      {po.paymentMethod || "None Specified"}
                                    </p>
                                  </div>

                                  {/* Payment Status Dropdown Selector */}
                                  <div>
                                    <h4 style={{ margin: "0 0 0.5rem 0", color: "var(--text-primary)", fontSize: "0.95rem" }}>Update Payment Status</h4>
                                    <select
                                      className="form-input"
                                      value={payStatus}
                                      onChange={(e) => handleUpdatePaymentStatus(po.id, e.target.value)}
                                      style={{ margin: 0, padding: "0.25rem 0.5rem", fontSize: "0.85rem", height: "auto", minHeight: "auto" }}
                                    >
                                      <option value="Unpaid">Unpaid</option>
                                      <option value="Paid">Paid</option>
                                      <option value="Partial">Partial</option>
                                      <option value="Refunded">Refunded</option>
                                    </select>
                                  </div>

                                </div>
                              </div>

                              {/* Items Breakdown Table */}
                              <div>
                                <h4 style={{ margin: "0 0 0.75rem 0", color: "var(--text-primary)", fontSize: "0.95rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                                  PO Line Items
                                </h4>
                                <div style={{ border: "1px solid var(--border-color)", borderRadius: "4px", overflow: "hidden" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", margin: 0 }}>
                                    <thead>
                                      <tr style={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
                                        <th style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)", textAlign: "left" }}>Description</th>
                                        <th style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)", textAlign: "center", width: "80px" }}>Qty</th>
                                        <th style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)", textAlign: "left" }}>Vendor</th>
                                        <th style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)", textAlign: "right", width: "120px" }}>Vendor Cost</th>
                                        <th style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)", textAlign: "right", width: "120px" }}>Total Cost</th>
                                        <th style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)", textAlign: "center", width: "160px" }}>Logistics Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {!po.items || po.items.length === 0 ? (
                                        <tr>
                                          <td colSpan="6" style={{ padding: "1rem", textAlign: "center", color: "var(--text-muted)" }}>
                                            No items on this PO.
                                          </td>
                                        </tr>
                                      ) : (
                                        (() => {
                                          const rows = [];
                                          po.items.forEach(item => {
                                            if (item.vendors && item.vendors.length > 0) {
                                              item.vendors.forEach(v => {
                                                const key = `${item.id}_${v.id}`;
                                                const itemLogStatus = shipment?.itemStatuses?.[key]?.status || shipment?.status || "To Order";
                                                rows.push({
                                                  id: key,
                                                  description: `${item.lineNumber || "01"} - ${item.description || item.name || "Unnamed Item"}`,
                                                  qty: Number(v.quantity || 0),
                                                  vendor: v.vendor || "—",
                                                  vPrice: Number(v.vendorPrice || 0),
                                                  lineTotal: Number(v.quantity || 0) * Number(v.vendorPrice || 0) + Number(v.shippingCost || 0),
                                                  status: itemLogStatus
                                                });
                                              });
                                            } else {
                                              const itemLogStatus = shipment?.itemStatuses?.[item.id]?.status || shipment?.status || "To Order";
                                              rows.push({
                                                id: item.id,
                                                description: `${item.lineNumber || "01"} - ${item.description || item.name || "Unnamed Item"}`,
                                                qty: Number(item.quantity || 0),
                                                vendor: item.vendor || "—",
                                                vPrice: Number(item.vendorPrice || 0),
                                                lineTotal: Number(item.quantity || 0) * Number(item.vendorPrice || 0) + Number(item.shippingCost || 0),
                                                status: itemLogStatus
                                              });
                                            }
                                          });

                                          return rows.map(row => (
                                            <tr key={row.id} style={{ borderBottom: "1px solid var(--border-color)" }}>
                                              <td style={{ padding: "0.5rem 0.75rem", color: "var(--text-primary)" }}>{row.description}</td>
                                              <td style={{ padding: "0.5rem 0.75rem", textAlign: "center" }}>{row.qty}</td>
                                              <td style={{ padding: "0.5rem 0.75rem" }}>{row.vendor}</td>
                                              <td style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>${row.vPrice.toFixed(2)}</td>
                                              <td style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>${row.lineTotal.toFixed(2)}</td>
                                              <td style={{ padding: "0.5rem 0.75rem", textAlign: "center" }}>
                                                <span className={`badge ${getLogisticsBadgeClass(row.status)}`} style={{ fontSize: "0.75rem", padding: "0.15rem 0.4rem" }}>
                                                  {row.status}
                                                </span>
                                              </td>
                                            </tr>
                                          ));
                                        })()
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PO Notes Modal */}
      {selectedNotesPo && (
        <OrderNotesModal
          isOpen={!!selectedNotesPo}
          onClose={() => setSelectedNotesPo(null)}
          po={selectedNotesPo}
          currentUser={currentUser}
          onSaveNote={handleSavePoNote}
        />
      )}

    </div>
  );
}
